import api from "../utils/axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { showToast } from "../components/ui/toast";

/*
 * Admin deposit API.
 *
 * Every endpoint here is under /admin/deposit. The agent-facing equivalents
 * (/deposit/...) are gated to webUser/mobileUser and would 401 with an admin
 * token, so they deliberately have no hooks in this app.
 */

const DEPOSITS_KEY = ["deposits"];
const MONOOVA_EVENTS_KEY = ["monoovaEvents"];
const FLOAT_KEY = ["monoovaFloat"];
const LEDGERS_KEY = ["monoovaLedgers"];

// Strip empty filter values so they don't end up as ?status=&page= in the URL
const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "",
    ),
  );

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

/* ---------------------------------- Queries ---------------------------------- */

// GET every deposit, walking the server's 100-per-page limit.
// filters: { status, organizationId, propertyId, saleId, from, to }
// Returns { data: [...deposits] }
export const useAdminDepositsQuery = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: [...DEPOSITS_KEY, "admin", cleanParams(filters)],
    queryFn: async () => {
      const fetchPage = (page) =>
        api.get("/admin/deposit", {
          params: cleanParams({ ...filters, page, limit: 100 }),
        });
      const first = await fetchPage(1);
      const pages = first.data?.pagination?.pages || 1;
      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, i) => fetchPage(i + 2)),
      );
      return {
        data: [first, ...rest].flatMap((res) => res.data?.data || []),
      };
    },
    ...options,
  });
};

// GET the Monoova float balance — the pooled mAccount every per-sale virtual
// account sweeps into. Deposits in flight sit here until they are routed out.
// Returns { accountNumber, actualBalanceCents, clearedFundsOnly }.
export const useFloatBalanceQuery = (options = {}) => {
  return useQuery({
    queryKey: FLOAT_KEY,
    queryFn: async () => {
      const res = await api.get("/admin/deposit/float");
      return res.data?.data || null;
    },
    // Live money — refresh on an interval, but do not hammer Monoova.
    refetchInterval: 60000,
    // A 502 here means Monoova was unreachable, not that the page is broken.
    retry: 1,
    ...options,
  });
};

/*
 * GET every ledger Monoova holds for us, joined to the deposit that owns it.
 *
 * The point is the ones where `deposit` is null: a ledger holding real money
 * that our database has lost track of. Nothing else surfaces those — they are
 * absent from our records by definition, so they cannot be listed from our side.
 */
export const useLedgerAccountsQuery = (options = {}) => {
  return useQuery({
    queryKey: LEDGERS_KEY,
    queryFn: async () => {
      const res = await api.get("/admin/deposit/ledgers");
      return res.data?.data || [];
    },
    retry: 1,
    ...options,
  });
};

// GET raw Monoova webhook event log (paginated + filterable).
// filters: { eventName, processed, hasError, depositId, webhookId,
//            signatureValid, from, to, slim, page, limit }
export const useAdminMonoovaEventsQuery = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: [...MONOOVA_EVENTS_KEY, cleanParams(filters)],
    queryFn: async () => {
      const res = await api.get("/admin/deposit/events", {
        params: cleanParams(filters),
      });
      return {
        data: res.data?.data || [],
        pagination: res.data?.pagination || null,
      };
    },
    ...options,
  });
};

/*
 * GET what Monoova actually holds in this sale's ledger, alongside our own
 * figure and the difference.
 *
 * Ours is derived from the webhooks we received; this is read live from
 * Monoova. A non-zero difference means a delivery was missed — which is the
 * only way to see that, since a deposit that never heard about a payment looks
 * identical to one that was never paid.
 *
 * 409 for a pre-ledger deposit, whose funds sit in the pooled float instead.
 */
export const useDepositLedgerQuery = (depositId, options = {}) => {
  return useQuery({
    queryKey: [...DEPOSITS_KEY, depositId, "ledger"],
    queryFn: async () => {
      const res = await api.get(`/admin/deposit/${depositId}/ledger`);
      return res.data?.data || null;
    },
    enabled: !!depositId,
    // Live money, and a Monoova outage is not a broken page.
    retry: 1,
    ...options,
  });
};

// GET full webhook audit trail for one deposit.
// Returns { saleId, status, eventCount, events: [...] }
export const useDepositEventsQuery = (depositId, options = {}) => {
  return useQuery({
    queryKey: [...DEPOSITS_KEY, depositId, "events"],
    queryFn: async () => {
      const res = await api.get(`/admin/deposit/${depositId}/events`);
      return res.data?.data || null;
    },
    enabled: !!depositId,
    ...options,
  });
};

/* --------------------------------- Mutations --------------------------------- */
/* All three move real money or stop money arriving. */

// Re-run the float -> agency trust payout. The only exit from payout_failed.
export const useAdminRetryPayoutMutation = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await api.post(`/admin/deposit/${id}/retry-payout`);
      return res.data;
    },
    onSuccess: () => {
      showToast("success", "Payout re-initiated");
      queryClient.invalidateQueries({ queryKey: DEPOSITS_KEY });
    },
    onError: (error) => {
      showToast("error", errorMessage(error, "Failed to retry payout"));
    },
    ...options,
  });
};

// Refund the buyer — { id, reason, transactionId?, amountCents? }.
// transactionId selects WHICH incoming payment to refund (Monoova's
// TransactionId on the credit), defaulting to the first cleared one.
export const useAdminRefundDepositMutation = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const res = await api.post(`/admin/deposit/${id}/refund`, data);
      return res.data;
    },
    onSuccess: () => {
      showToast("success", "Refund initiated");
      queryClient.invalidateQueries({ queryKey: DEPOSITS_KEY });
    },
    onError: (error) => {
      showToast("error", errorMessage(error, "Failed to refund deposit"));
    },
    ...options,
  });
};

// Deactivate the receivable account — Monoova then rejects and auto-returns
// any further payment sent to it.
export const useAdminCancelDepositMutation = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const res = await api.post(`/admin/deposit/${id}/cancel`, { reason });
      return res.data;
    },
    onSuccess: () => {
      showToast(
        "success",
        "Deposit cancelled — the account no longer accepts payment",
      );
      queryClient.invalidateQueries({ queryKey: DEPOSITS_KEY });
    },
    onError: (error) => {
      showToast("error", errorMessage(error, "Failed to cancel deposit"));
    },
    ...options,
  });
};

// Provisions a deposit — and therefore a PayID — for a property, before any
// buyer exists. The backend refuses unless the listing is live or sold, and
// refuses a second deposit on the same property.
export const useCreateDepositForPropertyMutation = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (propertyId) => {
      const res = await api.post("/admin/deposit", { propertyId });
      return res.data;
    },
    onSuccess: () => {
      // The PayID is not ready yet: provisioning is asynchronous and a cron
      // registers it once Monoova activates NPP. Saying "created" alone has
      // admins refreshing for a PayID that takes minutes.
      showToast(
        "success",
        "Deposit provisioning — the PayID appears once Monoova activates the account",
      );
      queryClient.invalidateQueries({ queryKey: DEPOSITS_KEY });
    },
    onError: (error) => {
      showToast("error", errorMessage(error, "Failed to create deposit"));
    },
    ...options,
  });
};
